/**
 * Минимальный безопасный markdown→HTML для контента Q&A/документов (design §7.1: заголовки,
 * списки, жирный/курсив, ссылки, картинки). Контент операторски-доверенный, но рендерится inline —
 * поэтому **защита от XSS обязательна** (§11).
 *
 * Стратегия «escape-first»: сначала экранируем весь HTML, потом накладываем whitelist-трансформы.
 * Сырой HTML в исходнике становится текстом (не тегами). URL ссылок/картинок пропускаем только
 * http(s) и относительные (`/…`) — `javascript:`/`data:` отсекаются. Полноценный markdown-парсер
 * НЕ тянем (bundle + XSS-поверхность) — набор намеренно узкий.
 */

// Сентинел вне печатного текста — не столкнётся с контентом при реинсерте плейсхолдеров.
const SENTINEL = '\u0000';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Разрешаем только http(s) и относительные ссылки; иначе null (отсечь javascript:/data:/…). */
function safeUrl(url: string): string | null {
  const u = url.trim();
  if (/^https?:\/\//i.test(u) || u.startsWith('/')) return escapeHtml(u);
  return null;
}

function renderInline(raw: string): string {
  const stash: string[] = [];
  const keep = (html: string) => {
    stash.push(html);
    return `${SENTINEL}${stash.length - 1}${SENTINEL}`;
  };

  // 1) картинки ![alt](url) — до ссылок (синтаксис вложен). Небезопасный src → выкидываем.
  let s = raw.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, alt: string, url: string) => {
    const src = safeUrl(url);
    return src ? keep(`<img src="${src}" alt="${escapeHtml(alt)}" loading="lazy">`) : '';
  });

  // 2) ссылки [text](url) — небезопасный href → остаётся просто текст.
  s = s.replace(/\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, text: string, url: string) => {
    const href = safeUrl(url);
    const label = escapeHtml(text);
    return href ? keep(`<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`) : label;
  });

  // 2.5) inline-код `…` — до общего экранирования, чтобы содержимое не ломалось трансформами
  //      жирного/курсива. Нужен для реквизитов: IBAN/номер счёта читают посимвольно, и пропорциональный
  //      шрифт превращает опечатку в потерянный перевод.
  s = s.replace(/`([^`\n]+)`/g, (_m, code) => keep(`<code>${escapeHtml(code)}</code>`));

  // 3) экранируем всё остальное (плейсхолдеры переживают — сентинел не спецсимвол HTML).
  s = escapeHtml(s);

  // 4) жирный/курсив по уже-экранированному тексту (контент внутри безопасен).
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // 5) возвращаем заранее собранный безопасный HTML картинок/ссылок.
  return s.replace(new RegExp(`${SENTINEL}(\\d+)${SENTINEL}`, 'g'), (_m, i: string) => stash[Number(i)]);
}

/**
 * Renders the markdown subset into a safe HTML string (for `v-html`).
 *
 * Spoiler blocks (collapsible sections for long answers — troubleshooting etc.):
 * a `+++ Title` line opens a block, a bare `+++` line closes it; regular markdown in between.
 * Rendered as native <details>/<summary> — no JS, works inside the FAQ accordion too.
 * No nesting: a repeated `+++ Title` closes the previous block first.
 */
export function renderMarkdown(md: string | null | undefined): string {
  if (!md) return '';
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let inSpoiler = false;

  const closeList = () => {
    if (listType) {
      out.push(`</${listType}>`);
      listType = null;
    }
  };
  const closeSpoiler = () => {
    if (inSpoiler) {
      closeList();
      out.push('</details>');
      inSpoiler = false;
    }
  };

  for (const line of lines) {
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const ordered = /^\d+\.\s+(.*)$/.exec(line);
    const spoilerOpen = /^\+\+\+\s+(.*\S)\s*$/.exec(line);

    if (spoilerOpen) {
      closeSpoiler();
      closeList();
      out.push(`<details><summary>${renderInline(spoilerOpen[1])}</summary>`);
      inSpoiler = true;
    } else if (line.trim() === '+++') {
      closeSpoiler();
    } else if (heading) {
      closeList();
      const level = heading[1].length;
      out.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
    } else if (bullet) {
      if (listType !== 'ul') { closeList(); out.push('<ul>'); listType = 'ul'; }
      out.push(`<li>${renderInline(bullet[1])}</li>`);
    } else if (ordered) {
      if (listType !== 'ol') { closeList(); out.push('<ol>'); listType = 'ol'; }
      out.push(`<li>${renderInline(ordered[1])}</li>`);
    } else if (line.trim() === '') {
      closeList();
    } else {
      closeList();
      out.push(`<p>${renderInline(line)}</p>`);
    }
  }
  closeList();
  closeSpoiler();
  return out.join('');
}
